// "I'm interested" / "Not interested" on an event (web/profile-ui.js): the big gold button on the full card, the small
// star on peeks, list rows and mini cards, and "Not interested", which hides the event and shows the toast.
import { requireAccount } from "../../profile/account";
import type { MouseEvent } from "react";
import { T } from "../../shared/config";
import { Icon } from "../../shared/icons";
import type { NowishEvent } from "../../shared/rules";
import { getProfile, useIsStarred } from "../../state/profile";
import { closeSheet, viewStore } from "../../state/view";
import { hideWithToast } from "./hideToastState";
import styles from "./profile.module.css";

/** The card's big button: "I'm interested" with a star, which turns into the filled "Interested". */
export function InterestedButton({ event }: { event: NowishEvent }) {
  const on = useIsStarred(event.id);
  return (
    <button type="button" id="interested" className={`${styles["btn"]} ${styles["interested"]} ${styles["primary"]}${on ? ` ${styles["on"]}` : ""}`}
      aria-pressed={on} onClick={() => requireAccount("interested", () => getProfile().toggleStar(event))}>
      <Icon name={on ? "star-filled" : "star"} />{T(on ? "card.interestedOn" : "card.interested")}
    </button>
  );
}

/** The small star for peeks, list rows and mini cards. */
export function StarButton({ event, className }: { event: NowishEvent; place?: "peek" | "mini"; className?: string }) {
  const on = useIsStarred(event.id);
  const toggle = (ev: MouseEvent) => { ev.stopPropagation(); requireAccount("stars", () => getProfile().toggleStar(event)); };
  return (
    <button type="button" className={`${styles["star"]}${className ? ` ${className}` : ""}`} aria-pressed={on} data-star={event.id}
      aria-label={T(on ? "card.unstarEvent" : "card.starEvent")} title={T(on ? "card.interestedOn" : "card.interested")} onClick={toggle}>
      <Icon name={on ? "star-filled" : "star"} />
    </button>
  );
}

/** Hides it everywhere (map, lists, the phone's alerts), closes its card and shows the toast. */
export function NotInterestedButton({ event }: { event: NowishEvent }) {
  return (
    <button type="button" id="not-interested" className={styles["btn"]}
      onClick={() => requireAccount("notInterested", () => { hideWithToast(event); closeCard(event.id); })}>{T("card.notInterested")}</button>
  );
}

/** Closes the event's card, if it's the one open. */
function closeCard(id: string) {
  const sheet = viewStore.getState().sheet;
  if (sheet?.kind === "event" && sheet.id === id) closeSheet();
}
